import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { LoadingState } from '@/components/ui/states';
import {
  DELIVERY_LOCATION_CAPTURE_MODES,
  DELIVERY_PERMISSIONS,
  DELIVERY_ROUTES,
  apiMessage,
  type DeliveryLocationPolicy,
} from '../deliveryApi';

/**
 * ADR-0029 §2.9 — a driver's live position is OFF by default, and stays off
 * unless a venue opts in per branch (or tenant-wide, via `branchId: null`).
 * This is the one control surface for that: no map, no breadcrumb log — this
 * screen decides WHETHER a position is ever captured at all, and for how
 * long a captured one survives before the hourly sweep discards it. Reading
 * or setting it is its own permission (`delivery_location.configure`),
 * separate from `delivery_zones.*`, because a role that prices delivery zones
 * was never automatically meant to switch on continuous tracking of a person.
 */
export default function TrackingPolicyPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const canConfigure = can(DELIVERY_PERMISSIONS.deliveryLocationConfigure);

  const [branchId, setBranchId] = useState('');
  const [policy, setPolicy] = useState<DeliveryLocationPolicy | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = (branch: string) => {
    if (!branch) {
      setPolicy(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<DeliveryLocationPolicy>(DELIVERY_ROUTES.locationPolicy, { params: { branchId: branch } })
      .then(({ data }) => setPolicy(data))
      .catch((e) => {
        setPolicy(null);
        setError(apiMessage(e, t('delivery.tracking.loadFailed')));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(branchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const save = async () => {
    if (!policy || !branchId) return;
    setSaving(true);
    try {
      await api.patch(DELIVERY_ROUTES.locationPolicy, {
        branchId,
        captureMode: policy.captureMode,
        retentionHours: policy.retentionHours,
      });
      toast.success(t('delivery.tracking.saved'));
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.tracking.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <Field label={t('delivery.tracking.branch')} required hint={t('delivery.tracking.branchHint')}>
        <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
          <option value="">{t('delivery.tracking.branchPlaceholder')}</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </Select>
      </Field>

      {!branchId ? (
        <p className="text-sm text-ink-muted">{t('delivery.tracking.pickBranch')}</p>
      ) : loading ? (
        <LoadingState />
      ) : error ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50/70 p-3 text-sm dark:border-red-900 dark:bg-red-950/30"
        >
          <Icon as={TriangleAlert} size="sm" className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
          <span>
            {error}{' '}
            <button className="underline" onClick={() => load(branchId)}>
              {t('common.tryAgain')}
            </button>
          </span>
        </div>
      ) : policy ? (
        <div className="space-y-4 rounded-xl border bg-surface p-4 shadow-sm">
          <Field label={t('delivery.tracking.captureMode')} hint={t('delivery.tracking.captureModeHint')}>
            <Select
              value={policy.captureMode}
              disabled={!canConfigure}
              onChange={(e) =>
                setPolicy({ ...policy, captureMode: e.target.value as DeliveryLocationPolicy['captureMode'] })
              }
            >
              {DELIVERY_LOCATION_CAPTURE_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`delivery.captureMode.${mode}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('delivery.tracking.retentionHours')} hint={t('delivery.tracking.retentionHoursHint')}>
            <Input
              type="number"
              min={1}
              max={24 * 30}
              disabled={!canConfigure}
              value={policy.retentionHours}
              onChange={(e) => setPolicy({ ...policy, retentionHours: Number(e.target.value) || 1 })}
            />
          </Field>
          {policy.captureMode === 'OFF' && (
            <p className="text-xs text-ink-muted">{t('delivery.tracking.offHint')}</p>
          )}
          {canConfigure && (
            <Button onClick={save} disabled={saving}>
              {t('common.save')}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
